import { formatCurrency } from '../domain/format';
import { aggregateAnnualScenario } from '../domain/scenarioEngine';
import type { MonthlyResult } from '../domain/types';

const DISPLAY_YEARS = ['2026', '2027', '2028'] as const;

const periodLabel = (actualMonths: number, forecastMonths: number) => {
  if (actualMonths && forecastMonths) return `${actualMonths} actual + ${forecastMonths} forecast`;
  if (actualMonths) return `${actualMonths} actual months`;
  return `${forecastMonths} forecast months`;
};

export const AnnualImpactTable = ({
  months,
  id,
}: {
  months: MonthlyResult[];
  id: string;
}) => {
  const annual = aggregateAnnualScenario(months, DISPLAY_YEARS);
  const hasExpenseImpact = annual.some((item) => Math.abs(item.savings) >= 0.005);
  const hasRevenueImpact = annual.some((item) => Math.abs(item.revenueImpact) >= 0.005);
  const updateExplanation = hasExpenseImpact && !hasRevenueImpact
    ? 'Cost-cut scenario active: plan and revenue rows stay fixed. Moving the cut month changes Scenario expenses, Savings from cuts, and Scenario modeled profit.'
    : hasRevenueImpact && !hasExpenseImpact
      ? 'Revenue scenario active: plan and expense rows stay fixed. Moving the start month changes Scenario revenue, Revenue impact, and Scenario modeled profit.'
      : hasRevenueImpact && hasExpenseImpact
        ? 'Cost and revenue scenarios are active. Plan rows stay fixed; the highlighted scenario and impact rows change by the affected months.'
        : 'Plan rows are the fixed reference. Turn on a cut or revenue change; the highlighted scenario and impact rows will update for the affected months.';
  const headingId = `${id}-annual-impact-heading`;
  const descriptionId = `${id}-annual-impact-description`;

  return (
    <section className="panel annual-impact-panel" aria-labelledby={headingId} aria-describedby={descriptionId}>
      <div className="panel-heading annual-impact-heading">
        <div>
          <p className="eyebrow">2026–2028 annual outlook</p>
          <h3 id={headingId}>See each year change as you adjust the scenario.</h3>
          <p id={descriptionId}>2026 combines locked January–June actuals with your July–December scenario. Later years are full-year forecasts.</p>
        </div>
        <span>Updates instantly</span>
      </div>
      <p className="annual-impact-explainer" role="status">{updateExplanation}</p>

      <div className="responsive-table annual-impact-table">
        <table>
          <caption className="sr-only">Annual plan revenue, scenario revenue, revenue impact, plan expenses, scenario expenses, savings, plan profit, and scenario profit for 2026 through 2028.</caption>
          <thead>
            <tr>
              <th scope="col">Annual result</th>
              {annual.map((item) => (
                <th key={item.year} scope="col" className="numeric">
                  <strong>{item.year}</strong>
                  <small>{periodLabel(item.actualMonths, item.forecastMonths)}</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Plan revenue</th>
              {annual.map((item) => <td key={item.year} className="numeric">{formatCurrency(item.planRevenue)}</td>)}
            </tr>
            <tr className="annual-scenario-row">
              <th scope="row">Scenario revenue</th>
              {annual.map((item) => <td key={item.year} className="numeric">{formatCurrency(item.revenue)}</td>)}
            </tr>
            <tr className="annual-revenue-impact-row">
              <th scope="row">Revenue impact</th>
              {annual.map((item) => <td key={item.year} className={`numeric ${item.revenueImpact >= 0 ? 'positive-text' : 'negative-text'}`}><strong>{formatCurrency(item.revenueImpact)}</strong></td>)}
            </tr>
            <tr>
              <th scope="row">Plan expenses</th>
              {annual.map((item) => <td key={item.year} className="numeric">{formatCurrency(item.planExpenses)}</td>)}
            </tr>
            <tr className="annual-scenario-row">
              <th scope="row">Scenario expenses</th>
              {annual.map((item) => <td key={item.year} className="numeric"><strong>{formatCurrency(item.scenarioExpenses)}</strong></td>)}
            </tr>
            <tr className="annual-savings-row">
              <th scope="row">Savings from cuts</th>
              {annual.map((item) => <td key={item.year} className="numeric"><strong>{formatCurrency(item.savings)}</strong></td>)}
            </tr>
            <tr>
              <th scope="row">Plan modeled profit</th>
              {annual.map((item) => <td key={item.year} className="numeric">{formatCurrency(item.planProfit)}</td>)}
            </tr>
            <tr className="annual-profit-row">
              <th scope="row">Scenario modeled profit</th>
              {annual.map((item) => <td key={item.year} className="numeric"><strong>{formatCurrency(item.scenarioProfit)}</strong></td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};
